import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconAlert, IconCheck, IconClose } from './icons';
import { K } from '../kabinet/atoms';

/**
 * Ответ сервиса — там, где на него смотрят.
 *
 * ПОЧЕМУ ЭТО ПОНАДОБИЛОСЬ. Ответ на нажатие показывался плашкой в начале
 * страницы. Пока список умещался на экран, всё было честно. Сегодня над
 * ним встали восемь плиток и строка отбора, кампаний тридцать шесть,
 * и человек, нажавший тумблер на двадцатой строке, ответа НЕ ВИДИТ
 * ВООБЩЕ: тот уходит на два экрана вверх.
 *
 * Со стороны это выглядит как «нажимаю — ничего не происходит», и это
 * худший из возможных исходов: действие ушло на сервер, что-то поменяло,
 * а человек считает, что не нажалось, и нажимает снова. Ровно так
 * повторяют то, что повторять нельзя.
 *
 * ПОЭТОМУ ПЛАШКА ПРИКЛЕЕНА К ОКНУ, А НЕ К СТРАНИЦЕ. Она видна из любого
 * места списка, потому что относится не к месту, а ко времени: к тому,
 * что человек сделал секунду назад.
 *
 * САМА НЕ ИСЧЕЗАЕТ ПО ТАЙМЕРУ, ЕСЛИ ЭТО ОШИБКА. Успех можно и пропустить,
 * а сообщение о неудаче обязано дождаться, пока его прочитают: оно
 * единственное объясняет, почему кампания осталась работать.
 */

export function Toast({
  text,
  kind = 'ok',
  onClose,
}: {
  text: string | null;
  /**
   * ТРИ ВИДА, А НЕ ДВА (передача 10.09.2026).
   *
   * `ok` — сделано, `bad` — не вышло. Третий, `warn`, — «так нельзя,
   * и вот почему»: нажатие по запертой галочке прав, кнопка приглашения
   * без адреса. Это не отказ сервера и не успех; красная плашка здесь
   * читалась бы как поломка, зелёная — как «получилось».
   *
   * Эталон зовёт его тем же словом: `this.toast('warn', …)`.
   */
  kind?: 'ok' | 'bad' | 'warn';
  onClose: () => void;
}) {
  useEffect(() => {
    /**
     * УСПЕХ ГАСНЕТ САМ ЧЕРЕЗ ЧЕТЫРЕ СЕКУНДЫ, ОШИБКА — НЕТ.
     *
     * Четыре — из эталона; у нас стояло восемь. Разница видна на серии
     * действий: правишь ставки подряд, и восьмисекундная плашка ещё
     * висит от предыдущей, когда приходит ответ на следующую.
     *
     * ОТКАЗ ОСТАЁТСЯ, И ЭТОГО В ЭТАЛОНЕ НЕТ, потому что там нет отказов.
     * Сообщение о неудаче обязано дождаться, пока его прочитают: оно
     * единственное объясняет, почему кампания осталась работать.
     */
    // Гаснет само только `ok`: и отказ, и объяснение обязаны дождаться,
    // пока их прочитают.
    if (text === null || kind !== 'ok') return;
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [text, kind, onClose]);

  useEffect(() => {
    if (text === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [text, onClose]);

  if (text === null) return null;

  // ЛЕВАЯ КРОМКА ЦВЕТОМ, А НЕ ВЕСЬ ФОН. Залитая плашка поверх таблицы
  // прячет под собой две строки; кромка в три пикселя различает успех
  // и отказ так же надёжно, а читается поверх обеих тем одинаково.
  const edge =
    kind === 'bad' ? 'var(--bad)' : kind === 'warn' ? 'var(--warn)' : 'var(--good)';

  return createPortal(
    /* ═══════════════════════════════════════════════════════════════════
       ДВА УЗЛА, А НЕ ОДИН, И ЭТО НЕ ЛИШНЯЯ ОБЁРТКА.

       Внешний прибит к окну во всю ширину и НИЧЕГО НЕ ЛОВИТ
       (`pointer-events: none` в частице): без этого невидимая полоса
       поверх таблицы съедала бы нажатия по нижним строкам. Внутренняя
       карточка ловит сама (`pointer-events: auto`) — у неё крестик.

       У нас был один узел, прижатый серединой (`left: 50 %` и сдвиг),
       и ловил он всё. Так в эталоне и по делу.
       ═══════════════════════════════════════════════════════════════ */
    /* `data-kab` НА ОБЁРТКЕ, А НЕ НА САМОЙ ПЛАШКЕ: правила частиц
       живут в области `[data-kab] .k-…`, то есть требуют атрибут
       у ПРЕДКА. Пока он стоял на том же узле, что и класс, ни одно
       правило не применялось — плашка выходила обычным блоком в потоке
       страницы. Обёртка ничего не весит: сама плашка прибита к окну. */
    <div data-kab>
      <div data-d="плашка-уведомления" className={K.плашкаУведомления}>
        <div
          role="status"
          aria-live="polite"
          className={K.карточкаУведомления}
          /* КРОМКА ПЕРЕКРАШИВАЕТСЯ НА МЕСТЕ. Частица вырезана из плашки
           успеха и несёт `border-left: 3px solid var(--good)`; отказ
           тем же узлом, но красной кромкой — второй частицы для этого
           заводить неоткуда: эталон отказов не рисует. */
          style={{ borderLeftColor: edge }}
        >
          <span className={K.значокУведомления} style={{ color: edge }}>
            {kind === 'ok' ? <IconCheck size={16} /> : <IconAlert size={16} />}
          </span>
          <span className={K.текстУведомления}>{text}</span>
          <button onClick={onClose} aria-label="Закрыть" className={K.крестикУведомления}>
            <IconClose size={14} />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
